/**
 * Cookie Management Service
 * Handles actual cookie collection, analytics, and marketing integrations
 */

import { logger } from '@/utils/logger'

export interface CookiePreferences {
  necessary: boolean
  functional: boolean
  analytics: boolean
  marketing: boolean
}

class CookieService {
  private preferences: CookiePreferences = {
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false
  }

  private readonly PREFERENCES_KEY = 'cookie_preferences'
  private readonly CONSENT_KEY = 'cookie_consent'

  /**
   * Load saved cookie preferences
   */
  loadPreferences(): CookiePreferences {
    const saved = localStorage.getItem(this.PREFERENCES_KEY)
    if (saved) {
      try {
        this.preferences = JSON.parse(saved)
      } catch (e) {
        console.error('Failed to parse cookie preferences:', e)
      }
    }
    return this.preferences
  }

  /**
   * Save cookie preferences and apply them
   */
  savePreferences(prefs: CookiePreferences): void {
    this.preferences = prefs
    localStorage.setItem(this.PREFERENCES_KEY, JSON.stringify(prefs))
    localStorage.setItem(this.CONSENT_KEY, 'true')
    
    // Apply the preferences
    this.applyPreferences()
  }

  /**
   * Apply cookie preferences by enabling/disabling tracking
   */
  private applyPreferences(): void {
    // Functional cookies
    if (this.preferences.functional) {
      this.enableFunctionalCookies()
    } else {
      this.disableFunctionalCookies()
    }

    // Analytics cookies
    if (this.preferences.analytics) {
      this.enableAnalytics()
    } else {
      this.disableAnalytics()
    }

    // Marketing cookies
    if (this.preferences.marketing) {
      this.enableMarketing()
    } else {
      this.disableMarketing()
    }
  }

  /**
   * Enable functional cookies
   * These store user preferences like theme, language, etc.
   */
  private enableFunctionalCookies(): void {
    console.log('✅ Functional cookies enabled')
    
    // Set a functional cookie to remember user preferences
    this.setCookie('user_preferences_enabled', 'true', 365)
    
    // You can store additional preferences in localStorage or cookies
    // Example: theme preference, language preference, etc.
  }

  /**
   * Disable functional cookies
   */
  private disableFunctionalCookies(): void {
    console.log('❌ Functional cookies disabled')
    this.deleteCookie('user_preferences_enabled')
    
    // Clear functional data from localStorage (except essential auth tokens)
    const keysToKeep = ['auth_token', 'cookie_consent', 'cookie_preferences']
    Object.keys(localStorage).forEach(key => {
      if (!keysToKeep.includes(key)) {
        // Only remove non-essential items
        if (!key.startsWith('auth_') && !key.startsWith('token_')) {
          localStorage.removeItem(key)
        }
      }
    })
  }

  /**
   * Enable analytics tracking (Google Analytics, etc.)
   */
  private enableAnalytics(): void {
    console.log('✅ Analytics cookies enabled')
    
    // Set analytics consent cookie
    this.setCookie('analytics_enabled', 'true', 365)
    
    // Initialize Google Analytics if gtag is available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        'analytics_storage': 'granted'
      })
      console.log('📊 Google Analytics enabled')
    }

    // Track page views
    this.trackPageView()
    
    // Set up analytics tracking
    this.initializeAnalytics()
  }

  /**
   * Disable analytics tracking
   */
  private disableAnalytics(): void {
    console.log('❌ Analytics cookies disabled')
    this.deleteCookie('analytics_enabled')
    this.deleteCookie('_ga')
    this.deleteCookie('_gid')
    this.deleteCookie('_gat')
    
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        'analytics_storage': 'denied'
      })
      console.log('📊 Google Analytics disabled')
    }
  }

  /**
   * Enable marketing cookies
   */
  private enableMarketing(): void {
    console.log('✅ Marketing cookies enabled')
    
    this.setCookie('marketing_enabled', 'true', 90)
    
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted'
      })
      console.log('📢 Marketing tracking enabled')
    }

    // Track marketing source if available
    this.trackMarketingSource()
  }

  /**
   * Disable marketing cookies
   */
  private disableMarketing(): void {
    console.log('❌ Marketing cookies disabled')
    this.deleteCookie('marketing_enabled')
    this.deleteCookie('marketing_id')
    this.deleteCookie('referrer')
    
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied'
      })
      console.log('📢 Marketing tracking disabled')
    }
  }

  /**
   * Initialize analytics tracking
   */
  private initializeAnalytics(): void {
    if (!this.preferences.analytics) return

    // Track user session
    const sessionId = this.getOrCreateSessionId()
    this.setCookie('analytics_session', sessionId, 1) // 1 day

    // Set up event listeners for analytics
    if (typeof window !== 'undefined') {
      // Track clicks on important elements
      document.addEventListener('click', (e) => {
        const target = e.target as HTMLElement
        if (target.matches('button[data-track], a[data-track]')) {
          this.trackEvent('click', {
            element: target.getAttribute('data-track') || target.textContent,
            url: window.location.pathname
          })
        }
      })
    }
  }

  /**
   * Track marketing source from URL parameters
   */
  private trackMarketingSource(): void {
    if (!this.preferences.marketing) return

    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search)
      const source = urlParams.get('utm_source')
      const medium = urlParams.get('utm_medium')
      const campaign = urlParams.get('utm_campaign')

      if (source || medium || campaign) {
        const marketingData = {
          source: source || 'unknown',
          medium: medium || 'unknown',
          campaign: campaign || 'unknown',
          timestamp: Date.now()
        }
        
        localStorage.setItem('marketing_source', JSON.stringify(marketingData))
        this.setCookie('referrer', source || 'direct', 30)
        
        console.log('📢 Marketing source tracked:', marketingData)
      }
    }
  }

  /**
   * Track page view
   */
  trackPageView(): void {
    if (!this.preferences.analytics) return

    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'page_view', {
        page_path: window.location.pathname,
        page_title: document.title
      })
    }

    // Custom analytics
    this.trackEvent('page_view', {
      path: window.location.pathname,
      title: document.title,
      referrer: document.referrer
    })
  }

  /**
   * Track custom event
   */
  trackEvent(eventName: string, data?: Record<string, any>): void {
    if (!this.preferences.analytics) return

    logger.debug('Event tracked:', eventName, data)

    // Send to Google Analytics if available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', eventName, data)
    }

    // You can also send to your own analytics endpoint
    // fetch('/api/analytics/event', {
    //   method: 'POST',
    //   body: JSON.stringify({ event: eventName, data, timestamp: Date.now() })
    // })
  }

  /**
   * Get or create session ID
   */
  private getOrCreateSessionId(): string {
    let sessionId = this.getCookie('analytics_session')
    if (!sessionId) {
      sessionId = this.generateSessionId()
    }
    return sessionId
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Set a cookie
   */
  private setCookie(name: string, value: string, days: number): void {
    const expires = new Date()
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`
  }

  /**
   * Get a cookie value
   */
  private getCookie(name: string): string | null {
    const nameEQ = name + '='
    const ca = document.cookie.split(';')
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i]
      while (c.charAt(0) === ' ') c = c.substring(1, c.length)
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
    }
    return null
  }

  /**
   * Delete a cookie
   */
  private deleteCookie(name: string): void {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`
  }

  /**
   * Check if user has consented
   */
  hasConsented(): boolean {
    return localStorage.getItem(this.CONSENT_KEY) === 'true'
  }

  /**
   * Get current preferences
   */
  getPreferences(): CookiePreferences {
    return { ...this.preferences }
  }
}

export const cookieService = new CookieService()
